import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Car, Pencil, Plus } from 'lucide-react'
import { getContentList } from '../../lib/api/content'
import { PageHead, Stat } from './ui'
import { TransportEditDrawer } from './TransportEditDrawer'

type El = Record<string, unknown>
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0)
const fmtCap = (n: number) => (n >= 9999 ? 'Illimitato' : String(n))

function stripHtml(v: unknown): string {
  return String(v ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}

type Row = { id: string; name: string; desc: string; maxAvailability: number; maxOnsite: number; position: number }

export function TransportsPage() {
  const list = useQuery({
    queryKey: ['content', 'transport', 'all'],
    queryFn: () => getContentList('transport', { limit: 200 }),
  })
  const [editId, setEditId] = useState<string | null>(null)

  const rows = useMemo<Row[]>(() => {
    return (list.data?.data ?? [])
      .map((r) => {
        const el = r.elements as El
        const name = String(el.name ?? el.label ?? 'Senza nome')
        return {
          id: r.id,
          name,
          desc: stripHtml(el.description),
          maxAvailability: num(el.maxAvailability),
          maxOnsite: num(el.maxOnsitePaymentAvailability),
          position: num(el.position),
        }
      })
      .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'it'))
  }, [list.data])

  const totalCapacity = useMemo(() => rows.reduce((s, r) => s + r.maxAvailability, 0), [rows])

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Tipi di veicolo"
        description="Categorie di veicolo selezionabili in prenotazione, con la disponibilità massima giornaliera e il limite di pagamenti in cassa."
        model="transport"
      />

      <div className="mb-[18px] grid grid-cols-2 gap-[14px] sm:grid-cols-3">
        <Stat label="Tipi di veicolo" value={String(rows.length)} sub="categorie configurate" />
        <Stat label="Capienza giornaliera" value={totalCapacity.toLocaleString('it-IT')} sub="posti totali/giorno" />
        <button
          onClick={() => setEditId('new')}
          className="flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3.5 text-[13px] font-bold text-white transition hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" /> Aggiungi tipo di veicolo
        </button>
      </div>

      <p className="mb-2 flex items-center gap-1.5 text-[12px] text-muted">
        <Pencil className="h-3.5 w-3.5" /> Clicca una riga per modificare
      </p>
      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare i tipi di veicolo.
        </div>
      ) : (
        <div className="cv-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-line-soft text-[11px] font-bold uppercase tracking-[1px] text-muted">
                  <th className="px-5 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-right">Disponibilità giornaliera</th>
                  <th className="px-4 py-3 text-right">Pagabili in cassa</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className="border-b border-line-soft last:border-0">
                      {Array.from({ length: 3 }).map((_c, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="skeleton h-3.5 w-28 rounded-sm bg-line-soft" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-5 py-16 text-center text-sm text-muted">
                      Nessun tipo di veicolo configurato.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setEditId(r.id)}
                      className="cursor-pointer border-b border-line-soft last:border-0 hover:bg-paper"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
                            <Car className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="font-semibold text-coal">{r.name}</div>
                            {r.desc && <div className="truncate text-[12px] text-muted">{r.desc}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold tnum text-coal">
                        {r.maxAvailability}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tnum text-slate">{fmtCap(r.maxOnsite)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TransportEditDrawer transportId={editId} onClose={() => setEditId(null)} />
    </div>
  )
}
